import { useParams } from "react-router-dom"
import { useDispatch } from "react-redux"
import { useEffect, useState } from "react"
import { showToastMessage } from "../../../../stateManagement/reducers"
import ToastMessageContainer from "../../../../widgets/toastMessageContainer/ToastMessageContainer"
import LoaderWrapper from "../../../../widgets/loaderWrapper/LoaderWrapper"
import TableWidget from "../../../../widgets/table/tableWidget/TableWidget"
import { prepareTableData } from "../../../../features/dataFormatFunctions/dataFormatter"
import { configureTableLazyState, defineTableColumnsFromArray, defineTableFilters } from "../../../../widgets/table/tableFeatures/tableFunctions"
import ContentHeader from "../../../../widgets/contentHeader/ContentHeader"
import { LandLotOwnersHeaderColumnGroupTemplate } from "../../../../widgets/table/tableFeatures/tableTemplates/tableHeaderColumnGroupTemplates/landLotOwners"
import { buildFileDownloadBodyTemplate, buildFilePreviewBodyTemplate, filePreviewBodyTemplate } from "../../../../widgets/table/tableFeatures/tableTemplates/tableBodyTemplates/documents"
import { useLandLotOwnersWithDocuments } from "../../../../hooks/data/useLandLotOwnersWithDocuments"
import { useLandLotOwners } from "../../../../hooks/data/useLandLotOwners"
import { downloadDocumentFile } from "../../tableDocuments/tableDocumentsFunctions"
import { useAppSelector } from "../../../../stateManagement/hooks/redux"
import { navigateTo } from "../../../../widgets/navigationService/navigationServiceFunctions"

const columnsStructure: any = [
    {
        headerName: "Правообладатель",
        field: "UserName",
        align: "center",
        valueType: "string"
    },
    {
        headerName: "Право",
        field: "RightTypeString",
        align: "center",
        valueType: "string",
    },
    {
        headerName: "Реквизиты",
        field: "RightCertificationDocRequisites",
        align: "center",
        valueType: "string",
        shouldBeFormatted: false,
    },
    {
        headerName: "Просмотреть",
        field: "RightCertificationDocPreviewData",
        align: "center",
        valueType: "string",
        bodyTemplate: buildFilePreviewBodyTemplate({
            propForDocumentId: "DocID",
            propForFileExtension: "RightCertificationDocFileExt",
            previewFunction(id) {
                navigateTo(`documents/${id}`, { relative: true })
            },
        })
    },
    {
        headerName: "Скачать",
        field: "RightCertificationDocDownloadData",
        align: "center",
        valueType: "string",
    },
    {
        headerName: "Реквизиты",
        field: "RightEstablishmentDocRequisites",
        align: "center",
        valueType: "string",
        shouldBeFormatted: false,
    },
    {
        headerName: "Просмотреть",
        field: "RightEstablishmentDocPreviewData",
        align: "center",
        valueType: "string",
        bodyTemplate: buildFilePreviewBodyTemplate({
            propForDocumentId: "RightEstablishmentDocID",
            propForFileExtension: "RightEstablishmentDocFileExt",
            previewFunction(id) {
                navigateTo(`documents/${id}`, { relative: true })
            },
        })
    },
    {
        headerName: "Скачать",
        field: "RightEstablishmentDocDownloadData",
        align: "center",
        valueType: "string",
    }
]

export default function TableLandLotOwners() {
    const objectTypeName = "LandLotOwners"
    const dispatch = useDispatch()
    const [tableValue, setTableValue] = useState<any>([])
    const [columns, setColumns] = useState<any>([])
    const headerColumnGroup = LandLotOwnersHeaderColumnGroupTemplate()
    const { landLotId } = useParams()

    const { owners: rawOwners, isLoading: isLoadingOwners, isError: isErrorOwners } = useLandLotOwners(landLotId);

    const { owners: ownersWithDocuments, isFetching: isFetchingDocs } = useLandLotOwnersWithDocuments(rawOwners);

    const { documentsDownloadingState } = useAppSelector(state => state.documents) ?? {}

    useEffect(() => {
        let cols = defineTableColumnsFromArray(columnsStructure)

        // RCD - Right Certification Doc
        const RCDFileDownloadColIndex = cols.findIndex(
            (col: any) => col.field === 'RightCertificationDocDownloadData'
        );

        // RED - Right Establishment Doc
        const REDFileDownloadColIndex = cols.findIndex(
            (col: any) => col.field === 'RightEstablishmentDocDownloadData'
        );

        const RCDFileDownloadBodyTemplate = buildFileDownloadBodyTemplate({
            propForDocumentId: 'DocID',
            propForFileExtension: 'RightCertificationDocFileExt',
            propForDocumentName: 'RightCertificationDocRequisites',
            propForLinkType: "RightCertificationDocLinkType",
            downloadFunction: downloadDocumentFile,
            documentsDownloadingState,
        });

        const REDFileDownloadBodyTemplate = buildFileDownloadBodyTemplate({
            propForDocumentId: 'RightEstablishmentDocID',
            propForFileExtension: 'RightEstablishmentDocFileExt',
            propForDocumentName: 'RightEstablishmentDocRequisites',
            propForLinkType: "RightEstablishmentDocLinkType",
            downloadFunction: downloadDocumentFile,
            documentsDownloadingState,
        });

        cols[RCDFileDownloadColIndex] = {
            ...cols[RCDFileDownloadColIndex],
            bodyTemplate: RCDFileDownloadBodyTemplate,
        };

        cols[REDFileDownloadColIndex] = {
            ...cols[REDFileDownloadColIndex],
            bodyTemplate: REDFileDownloadBodyTemplate,
        };

        setColumns(cols);
    }, [documentsDownloadingState])

    useEffect(() => {
        if (ownersWithDocuments && columns) {
            let value = prepareTableData(ownersWithDocuments, columns)
            setTableValue(value)
        }
    }, [ownersWithDocuments, columns])

    useEffect(() => {
        if (isErrorOwners) {
            dispatch(showToastMessage({ messageText: "Ошибка загрузки таблицы", messageType: "error" }));
        }
    }, [isErrorOwners]);

    return (
        <>
            <ContentHeader title="Права на земельный участок" type="simple" />
            <div className="table-widget-with-toast with-content-header">
                <ToastMessageContainer />
                <LoaderWrapper isLoading={isLoadingOwners || isFetchingDocs} >
                    <TableWidget
                        tableValue={tableValue}
                        columns={columns}
                        pageSize={100}
                        withTitleRow
                        headerColumnGroup={headerColumnGroup}
                    />
                </LoaderWrapper>
            </div>
        </>
    )
}
